import { useState, useRef, ChangeEvent, DragEvent } from 'react'
import { Upload, X, Image as ImageIcon, Loader2, Check, AlertCircle, Link as LinkIcon } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

interface CoverImageUploaderProps {
  value: string
  onChange: (url: string) => void
  disabled?: boolean
}

export function CoverImageUploader({ value, onChange, disabled = false }: CoverImageUploaderProps) {
  const [isUploading, setIsUploading] = useState(false)
  const [isDragging, setIsDragging] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showManualUrl, setShowManualUrl] = useState(false)
  const fileInputRef = useRef<HTMLInputElement | null>(null)

  const uploadFile = async (file: File) => {
    // Validate file type
    const validTypes = ['image/png', 'image/jpeg', 'image/webp', 'image/svg+xml', 'image/gif']
    if (!validTypes.includes(file.type)) {
      setError('Please upload a valid image file (PNG, JPG, WEBP, SVG, or GIF).')
      return
    }

    // Validate size (10 MB max)
    if (file.size > 10 * 1024 * 1024) {
      setError('File size exceeds the 10 MB limit.')
      return
    }

    setError(null)
    setIsUploading(true)

    try {
      const formData = new FormData()
      formData.append('file', file)

      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''

      const response = await fetch('/admin/uploads', {
        method: 'POST',
        headers: {
          'X-CSRF-Token': csrfToken,
          'Accept': 'application/json',
        },
        body: formData,
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.error || 'Failed to upload image')
      }

      if (data.url) {
        onChange(data.url)
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'An error occurred during upload'
      setError(msg)
    } finally {
      setIsUploading(false)
    }
  }

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      uploadFile(file)
    }
    // reset input so same file can be re-selected if needed
    e.target.value = ''
  }

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    if (!disabled && !isUploading) {
      setIsDragging(true)
    }
  }

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
  }

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)

    if (disabled || isUploading) return

    const file = e.dataTransfer.files?.[0]
    if (file) {
      uploadFile(file)
    }
  }

  const handleRemove = () => {
    onChange('')
    setError(null)
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <Label htmlFor="cover_image_url" className="text-xs font-semibold text-foreground flex items-center gap-1.5">
          <ImageIcon className="w-3.5 h-3.5 text-primary" />
          <span>Cover Image</span>
        </Label>
        <button
          type="button"
          onClick={() => setShowManualUrl(prev => !prev)}
          className="text-2xs text-muted-foreground hover:text-foreground font-medium flex items-center gap-1 cursor-pointer transition-colors"
        >
          <LinkIcon className="w-3 h-3" />
          <span>{showManualUrl ? 'Hide Direct URL' : 'Enter URL Manually'}</span>
        </button>
      </div>

      {/* Hidden File Input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/svg+xml,image/gif"
        onChange={handleFileChange}
        className="hidden"
        disabled={disabled || isUploading}
      />

      {/* Preview Card or Dropzone */}
      {value ? (
        <div className="relative rounded-2xl border border-border bg-muted/20 p-3 sm:p-4 space-y-3">
          <div className="relative aspect-21/9 sm:aspect-16/9 w-full max-h-64 rounded-xl overflow-hidden bg-muted border border-border flex items-center justify-center group shadow-xs">
            <img
              src={value}
              alt="Cover preview"
              className="w-full h-full object-cover object-center"
              onError={(e) => {
                // In case user entered an invalid direct URL
                (e.target as HTMLImageElement).src = ''
                setError('Unable to load image from URL. Please verify the link.')
              }}
            />
            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
              <Button
                type="button"
                size="sm"
                variant="secondary"
                disabled={disabled || isUploading}
                onClick={() => fileInputRef.current?.click()}
                className="h-8 text-xs font-semibold cursor-pointer shadow-sm"
              >
                <Upload className="w-3.5 h-3.5 mr-1" />
                Change Image
              </Button>
              <Button
                type="button"
                size="sm"
                variant="destructive"
                disabled={disabled || isUploading}
                onClick={handleRemove}
                className="h-8 text-xs font-semibold cursor-pointer shadow-sm"
              >
                <X className="w-3.5 h-3.5 mr-1" />
                Remove
              </Button>
            </div>
          </div>

          <div className="flex items-center justify-between gap-2 text-xs">
            <div className="flex items-center gap-1.5 text-muted-foreground truncate">
              <Check className="w-3.5 h-3.5 text-primary shrink-0" />
              <span className="font-mono text-2xs truncate select-all">{value}</span>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={disabled || isUploading}
                onClick={() => fileInputRef.current?.click()}
                className="h-7 px-2.5 text-2xs font-semibold cursor-pointer"
              >
                Replace
              </Button>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                disabled={disabled || isUploading}
                onClick={handleRemove}
                className="h-7 px-2 text-2xs text-destructive hover:bg-destructive/10 cursor-pointer"
              >
                Remove
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <div
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => {
            if (!disabled && !isUploading) {
              fileInputRef.current?.click()
            }
          }}
          className={`relative border-2 border-dashed rounded-2xl p-6 text-center cursor-pointer transition-all flex flex-col items-center justify-center gap-2.5 ${
            isDragging
              ? 'border-primary bg-primary/5'
              : 'border-border hover:border-primary/40 hover:bg-muted/30 bg-muted/10'
          } ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
        >
          {isUploading ? (
            <div className="py-4 flex flex-col items-center gap-2 text-primary">
              <Loader2 className="w-8 h-8 animate-spin" />
              <p className="text-xs font-semibold">Uploading image to server...</p>
            </div>
          ) : (
            <>
              <div className="w-11 h-11 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center text-primary">
                <Upload className="w-5 h-5" />
              </div>
              <div className="space-y-1">
                <p className="text-xs sm:text-sm font-semibold text-foreground">
                  <span className="text-primary hover:underline">Click to upload</span> or drag and drop cover image
                </p>
                <p className="text-2xs text-muted-foreground">
                  Supports PNG, JPG, WEBP, SVG, GIF up to 10 MB (Recommended 16:9 ratio, min 1200×630)
                </p>
              </div>
            </>
          )}
        </div>
      )}

      {/* Manual URL Input (collapsible or toggleable) */}
      {showManualUrl && (
        <div className="pt-1 space-y-1">
          <Input
            id="cover_image_url"
            value={value}
            onChange={(e) => {
              onChange(e.target.value)
              setError(null)
            }}
            placeholder="https://images.unsplash.com/..."
            className="text-xs font-mono h-9"
            disabled={disabled || isUploading}
          />
          <p className="text-3xs text-muted-foreground">
            You can paste an external CDN or image link directly.
          </p>
        </div>
      )}

      {/* Error message */}
      {error && (
        <div className="flex items-center gap-1.5 text-xs text-destructive bg-destructive/10 border border-destructive/20 rounded-xl px-3 py-2">
          <AlertCircle className="w-4 h-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}
    </div>
  )
}
